import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CdkTableModule } from '@angular/cdk/table';
import { InvitesDataSource } from './invites.datasource';
import { HeaderComponent } from '../shared/header.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EditInviteComponent } from './edit-invite.component';
import { ConfirmComponent } from '../shared/confirm.modal';
import { ToastrService } from 'ngx-toastr';
import { InvitesService } from './invites.service';
import { InviteDto } from '@oracychampions/api-interfaces';
import { AuthService } from '../auth/auth.service';

@Component({
  selector: 'oc-invites',
  standalone: true,
  imports: [CommonModule, RouterModule, CdkTableModule, HeaderComponent],
  template: `
    <div class="container-fluid page">
      <oc-header headerTitle="Invites">
        <button class="btn btn-primary btn-rounded" (click)="addInvite()">Add Invite</button>
      </oc-header>
      <cdk-table #table [dataSource]="dataSource!" class="oc-table">
        <ng-container cdkColumnDef="email">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Email </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.email }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="role">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Role </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            <span *ngIf="row.role === 0">Admin</span>
            <span *ngIf="row.role === 1">Teacher</span>
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="school">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> School </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.school.name }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="createDate">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> Created </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            {{ row.createDate | date }}
          </cdk-cell>
        </ng-container>

        <ng-container cdkColumnDef="actions">
          <cdk-header-cell *cdkHeaderCellDef class="oc-table-header-cell"> &nbsp; </cdk-header-cell>
          <cdk-cell *cdkCellDef="let row" class="oc-table-cell">
            <a (click)="deleteInvite(row)">Delete</a>
          </cdk-cell>
        </ng-container>

        <cdk-header-row *cdkHeaderRowDef="displayedColumns" class="oc-table-header-row"></cdk-header-row>
        <cdk-row *cdkRowDef="let row; columns: displayedColumns" class="oc-table-row"></cdk-row>
      </cdk-table>
    </div>
  `,
  styles: `
    
  `,
})
export class InvitesComponent {
  private invitesService = inject(InvitesService);
  private modalService = inject(NgbModal);
  private toastr = inject(ToastrService);
  private auth = inject(AuthService);
  private route = inject(ActivatedRoute);

  public schoolId: string | null = null;

  dataSource: InvitesDataSource | null = null;
  displayedColumns = ['email', 'role', 'school', 'createDate', 'actions'];

  constructor() {
    if (this.route.snapshot.data['scopeToSchool']) {
      this.schoolId = this.auth.auth$.getValue()?.user.schoolId;
      this.displayedColumns = ['email', 'role', 'createDate', 'actions'];
    }
    this.dataSource = new InvitesDataSource(this.invitesService, this.schoolId);
  }

  addInvite() {
    const modalRef = this.modalService.open(EditInviteComponent, { fullscreen: true });
    modalRef.componentInstance.schoolId = this.schoolId;
    modalRef.componentInstance.schoolId$.next(this.schoolId);
    modalRef.closed.subscribe(() => {
      this.invitesService.reloadInvites();
    });
  }

  editInvite(invite: InviteDto & { school: any }) {
    const modalRef = this.modalService.open(EditInviteComponent, { fullscreen: true });
    modalRef.componentInstance.id = invite.id;
    modalRef.componentInstance.model = {
      email: invite.email,
      role: invite.role,
      schoolId: { value: invite.schoolId, label: invite.school.name },
    };
    modalRef.closed.subscribe(() => {
      this.invitesService.reloadInvites();
    });
  }

  deleteInvite(invite: InviteDto) {
    const modalRef = this.modalService.open(ConfirmComponent);
    modalRef.closed.subscribe((confirmed) => {
      if (confirmed) {
        this.invitesService.deleteInvite(invite.id).subscribe(() => {
          this.toastr.success('Invite deleted');
          this.invitesService.reloadInvites();
        });
      }
    });
  }
}
